import React, { useEffect, useMemo } from 'react';
import _get from 'lodash/get';
import { useEdmDocumentationsIdsQuery } from 'graphql-common';
import { Option } from '@lib/interfaces/form';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import AssetFieldNames from '@lib/enums/fieldNames/assetFieldNames';

export default function useDocumentationLinksPrefill({
  formRef,
  assetId,
  initialDocumentationLinksRef,
  skip = false,
}: {
  formRef: FormRefType;
  assetId?: string;
  initialDocumentationLinksRef?: React.MutableRefObject<Option[]>;
  skip?: boolean;
}) {
  const { data, loading, error } = useEdmDocumentationsIdsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      onlyIds: true,
      filters: [
        {
          linkedAssetsId: {
            predicate: { eq: assetId || '' },
          },
        },
      ],
    },
    skip: !assetId || skip,
  });

  // Prefilled rows carry only `{ value: id }` — no `visibleViaQr`. The
  // baseline is loaded lazily by `DocumentsModalTableMultiselect` on open, and
  // the diff-based mutation preserves BE state for any row the user does not
  // explicitly toggle.
  const initialDocumentationLinks: Option[] = useMemo(() => {
    const ids = _get(data, 'data.collection', []) as Array<{ id: string }>;
    return ids.map(({ id }) => ({ value: id }));
  }, [data]);

  useEffect(() => {
    if (!assetId || !data) return;
    if (initialDocumentationLinksRef) {
      // eslint-disable-next-line no-param-reassign
      initialDocumentationLinksRef.current = initialDocumentationLinks;
    }
    if (formRef.current) {
      formRef.current.setValue(
        AssetFieldNames.DocumentationLinks,
        initialDocumentationLinks,
        { shouldDirty: false, shouldTouch: false, shouldValidate: false },
      );
    }
  }, [
    assetId,
    data,
    formRef,
    initialDocumentationLinks,
    initialDocumentationLinksRef,
  ]);

  return {
    loading: Boolean(assetId) && !skip && !error && (loading || !data),
  };
}
